<script setup lang="ts">
import { type Component, computed } from 'vue'

import type { IconStyleType, ImpactType } from '@/api/interfaces'
import type { ComparisonType } from '@/stores/interfaces/PublicData'

import AreaPlantedTreesIconSimple from '@/components/icons/comparisons/simple/AreaPlantedTreesIconSimple.vue'
import BathtubsIconSimple from '@/components/icons/comparisons/simple/BathtubsIconSimple.vue'
import CarbonSequesteredIconSimple from '@/components/icons/comparisons/simple/CarbonSequesteredIconSimple.vue'
import CoffeeCupsIconSimple from '@/components/icons/comparisons/simple/CoffeeCupsIconSimple.vue'
import FlightsIconSimple from '@/components/icons/comparisons/simple/FlightsIconSimple.vue'
import FootballPitchesIconSimple from '@/components/icons/comparisons/simple/FootballPitchesIconSimple.vue'
import DistanceIconSimple from '@/components/icons/comparisons/simple/DistanceIconSimple.vue'
import PlantBasedIconSimple from '@/components/icons/comparisons/simple/PlantBasedIconSimple.vue'
import StrawsIconSimple from '@/components/icons/comparisons/simple/StrawsIconSimple.vue'
import BabyWhalesIconSimple from '@/components/icons/comparisons/simple/BabyWhaleIconSimple.vue'
import IceHockeyRinksIconSimple from '@/components/icons/comparisons/simple/IceHockeyRinksIconSimple.vue'
import LitresIconSimple from '@/components/icons/comparisons/simple/LitresIconSimple.vue'
import BrighterEducationIconSimple from '@/components/icons/comparisons/simple/BrighterEducationIconSimple.vue'
import FetchingWaterIconSimple from '@/components/icons/comparisons/simple/FetchingWaterIconSimple.vue'
import HivesIconSimple from '@/components/icons/comparisons/simple/HivesIconSimple.vue'
import HoneyIconSimple from '@/components/icons/comparisons/simple/HoneyIconSimple.vue'
import FlowersIconSimple from '@/components/icons/comparisons/simple/FlowersIconSimple.vue'

import AreaPlantedIconFlat from '@/components/icons/comparisons/flat/AreaPlantedIconFlat.vue'
import BathtubsIconFlat from '@/components/icons/comparisons/flat/BathtubsIconFlat.vue'
import CarbonSequesteredIconFlat from '@/components/icons/comparisons/flat/CarbonSequesteredIconFlat.vue'
import CoffeeCupsIconFlat from '@/components/icons/comparisons/flat/CoffeeCupsIconFlat.vue'
import FlightsIconFlat from '@/components/icons/comparisons/flat/FlightsIconFlat.vue'
import FootballPitchesIconFlat from '@/components/icons/comparisons/flat/FootballPitchesIconFlat.vue'
import DistanceIconFlat from '@/components/icons/comparisons/flat/DistanceIconFlat.vue'
import PlantBasedIconFlat from '@/components/icons/comparisons/flat/PlantBasedIconFlat.vue'
import StrawsIconFlat from '@/components/icons/comparisons/flat/StrawsIconFlat.vue'
import BabyWhalesIconFlat from '@/components/icons/comparisons/flat/BabyWhalesIconFlat.vue'
import CarbonSequesteredKelpIconFlat from '@/components/icons/comparisons/flat/CarbonSequesteredKelpIconFlat.vue'
import IceHockeyRinksIconFlat from '@/components/icons/comparisons/flat/IceHockeyRinksIconFlat.vue'
import LitresIconFlat from '@/components/icons/comparisons/flat/LitresIconFlat.vue'
import BrighterEducationIconFlat from '@/components/icons/comparisons/flat/BrighterEducationIconFlat.vue'
import FetchingWaterIconFlat from '@/components/icons/comparisons/flat/FetchingWaterIconFlat.vue'
import HoneyIconFlat from '@/components/icons/comparisons/flat/HoneyIconFlat.vue'
import FlowersIconFlat from '@/components/icons/comparisons/flat/FlowersIconFlat.vue'
import HivesIconFlat from '@/components/icons/comparisons/flat/HivesIconFlat.vue'

import AreaPlantedIconIsometric from '@/components/icons/comparisons/isometric/AreaPlantedIconIsometric.vue'
import BathtubsIconIsometric from '@/components/icons/comparisons/isometric/BathtubsIconIsometric.vue'
import CarbonSequesteredIconIsometric from '@/components/icons/comparisons/isometric/CarbonSequesteredIconIsometric.vue'
import CoffeeCupsIconIsometric from '@/components/icons/comparisons/isometric/CoffeeCupsIconIsometric.vue'
import FlightsIconIsometric from '@/components/icons/comparisons/isometric/FlightsIconIsometric.vue'
import FootballPitchesIconIsometric from '@/components/icons/comparisons/isometric/FootballPitchesIconIsometric.vue'
import DistanceIconIsometric from '@/components/icons/comparisons/isometric/DistanceIconIsometric.vue'
import PlantBasedIconIsometric from '@/components/icons/comparisons/isometric/PlantBasedIconIsometric.vue'
import StrawsIconIsometric from '@/components/icons/comparisons/isometric/StrawsIconIsometric.vue'
import BabyWhalesIconIsometric from '@/components/icons/comparisons/isometric/BabyWhalesIconIsometric.vue'
import IceHockeyRinksIconIsometric from '@/components/icons/comparisons/isometric/IceHockeyRinksIconIsometric.vue'
import CarbonSequesteredKelpIconIsometric from '@/components/icons/comparisons/isometric/CarbonSequesteredKelpIconIsometric.vue'
import LitresIconIsometric from '@/components/icons/comparisons/isometric/LitresIconIsometric.vue'
import BrighterEducationIconIsometric from '@/components/icons/comparisons/isometric/BrighterEducationIconIsometric.vue'
import FetchingWaterIconIsometric from '@/components/icons/comparisons/isometric/FetchingWaterIconIsometric.vue'
import HivesIconIsometric from '@/components/icons/comparisons/isometric/HivesIconIsometric.vue'
import HoneyIconIsometric from '@/components/icons/comparisons/isometric/HoneyIconIsometric.vue'
import FlowersIconIsometric from '@/components/icons/comparisons/isometric/FlowersIconIsometric.vue'

interface Props {
  iconStyle: IconStyleType
  comparisonType: ComparisonType
  impactType: ImpactType
}

const props = defineProps<Props>()

const simpleIcons: Record<ImpactType, Partial<Record<ComparisonType, Component>>> = {
  carbon: {
    distance: DistanceIconSimple,
    flights: FlightsIconSimple,
    plantBased: PlantBasedIconSimple,
  },
  plastic: {
    straws: StrawsIconSimple,
    bathtubs: BathtubsIconSimple,
    coffeeCups: CoffeeCupsIconSimple,
  },
  trees: {
    carbonSequestered: CarbonSequesteredIconSimple,
    areaPlanted: AreaPlantedTreesIconSimple,
    footballPitches: FootballPitchesIconSimple,
  },
  kelp: {
    carbonSequestered: CarbonSequesteredIconSimple,
    iceHockeyRinks: IceHockeyRinksIconSimple,
    babyWhales: BabyWhalesIconSimple,
  },
  water: {
    brighterEducation: BrighterEducationIconSimple,
    fetchingWater: FetchingWaterIconSimple,
    litres: LitresIconSimple,
  },
  bees: {
    hives: HivesIconSimple,
    honey: HoneyIconSimple,
    flowers: FlowersIconSimple,
  },
}

const flatIcons: Record<ImpactType, Partial<Record<ComparisonType, Component>>> = {
  carbon: {
    distance: DistanceIconFlat,
    flights: FlightsIconFlat,
    plantBased: PlantBasedIconFlat,
  },
  plastic: {
    straws: StrawsIconFlat,
    bathtubs: BathtubsIconFlat,
    coffeeCups: CoffeeCupsIconFlat,
  },
  trees: {
    carbonSequestered: CarbonSequesteredIconFlat,
    areaPlanted: AreaPlantedIconFlat,
    footballPitches: FootballPitchesIconFlat,
  },
  kelp: {
    carbonSequestered: CarbonSequesteredKelpIconFlat,
    iceHockeyRinks: IceHockeyRinksIconFlat,
    babyWhales: BabyWhalesIconFlat,
  },
  water: {
    brighterEducation: BrighterEducationIconFlat,
    fetchingWater: FetchingWaterIconFlat,
    litres: LitresIconFlat,
  },
  bees: {
    hives: HivesIconFlat,
    honey: HoneyIconFlat,
    flowers: FlowersIconFlat,
  },
}

const isometricIcons: Record<ImpactType, Partial<Record<ComparisonType, Component>>> = {
  carbon: {
    distance: DistanceIconIsometric,
    flights: FlightsIconIsometric,
    plantBased: PlantBasedIconIsometric,
  },
  plastic: {
    straws: StrawsIconIsometric,
    bathtubs: BathtubsIconIsometric,
    coffeeCups: CoffeeCupsIconIsometric,
  },
  trees: {
    carbonSequestered: CarbonSequesteredIconIsometric,
    areaPlanted: AreaPlantedIconIsometric,
    footballPitches: FootballPitchesIconIsometric,
  },
  kelp: {
    carbonSequestered: CarbonSequesteredKelpIconIsometric,
    iceHockeyRinks: IceHockeyRinksIconIsometric,
    babyWhales: BabyWhalesIconIsometric,
  },
  water: {
    brighterEducation: BrighterEducationIconIsometric,
    fetchingWater: FetchingWaterIconIsometric,
    litres: LitresIconIsometric,
  },
  bees: {
    hives: HivesIconIsometric,
    honey: HoneyIconIsometric,
    flowers: FlowersIconIsometric,
  },
}

const icons: Record<
  IconStyleType,
  Record<ImpactType, Partial<Record<ComparisonType, Component>>>
> = {
  simple: simpleIcons,
  flat: flatIcons,
  isometric: isometricIcons,
}

const icon = computed(() => {
  return icons[props.iconStyle]?.[props.impactType]?.[props.comparisonType]
})
</script>

<template>
  <component :is="icon" />
</template>

<style scoped lang="scss"></style>
